import { useAppConstantsContext } from '@ltvco/refresh-lib/ctx';
import { Balance } from '@mui/icons-material';

import { type PACERDocketResult } from '../../types';
import {
  CardContainer,
  ContentContainer,
  HeaderRow,
  CardIconContainer,
  CardTitle,
  CourtCitation,
  DetailsContainer,
  DetailRow,
  DetailLabel,
  DetailValue,
  MoreCount,
  ViewButton,
  formatDate,
  getLimitedList,
} from '../shared';

const MAX_DISPLAY_ITEMS = 5;

export interface CasesResultCardProps {
  docketItem: PACERDocketResult;
  isHovered?: boolean;
}

export const CasesResultCard = ({
  docketItem,
  isHovered,
}: CasesResultCardProps) => {
  const { links } = useAppConstantsContext();
  const {
    court_citation_string,
    dateFiled,
    docketNumber,
    court,
    party,
    attorney,
  } = docketItem;

  const courtCitation =
    court_citation_string && dateFiled
      ? `(${court_citation_string} ${new Date(dateFiled).getFullYear()})`
      : '';
  const docketAndCourt = [docketNumber, court].filter(Boolean).join(' | ');
  const filedDate = dateFiled ? formatDate(dateFiled) : '';
  const partiesData = getLimitedList(party, MAX_DISPLAY_ITEMS);
  const attorneysData = getLimitedList(attorney, MAX_DISPLAY_ITEMS);

  const handleViewClick = () => {
    const url = new URL(
      `${links.baseUrl}/dashboard/court-cases/cases/${docketItem.docket_id}`
    );

    if (docketItem.assigned_to_id) {
      url.searchParams.set(
        'assigned_to_id',
        docketItem.assigned_to_id.toString()
      );
    }

    window.open(url.toString(), '_blank', 'noopener,noreferrer');
  };

  return (
    <CardContainer isHovered={isHovered} hoverColor="grey100">
      <ContentContainer>
        <HeaderRow>
          <CardIconContainer size={24}>
            <Balance />
          </CardIconContainer>
          <CardTitle>{docketItem.caseName}</CardTitle>
          {courtCitation && <CourtCitation>{courtCitation}</CourtCitation>}
        </HeaderRow>

        <DetailsContainer>
          {filedDate && (
            <DetailRow>
              <DetailLabel minWidth="110px" mobileMinWidth="70px">
                Filed:
              </DetailLabel>
              <DetailValue>{filedDate}</DetailValue>
            </DetailRow>
          )}

          {docketAndCourt && (
            <DetailRow>
              <DetailLabel minWidth="110px" mobileMinWidth="70px">
                Docket:
              </DetailLabel>
              <DetailValue>{docketAndCourt}</DetailValue>
            </DetailRow>
          )}

          {docketItem.suitNature && (
            <DetailRow>
              <DetailLabel minWidth="110px" mobileMinWidth="70px">
                Nature of Suit:
              </DetailLabel>
              <DetailValue>{docketItem.suitNature}</DetailValue>
            </DetailRow>
          )}

          {docketItem.assignedTo && (
            <DetailRow>
              <DetailLabel minWidth="110px" mobileMinWidth="70px">
                Assigned to:
              </DetailLabel>
              <DetailValue>{docketItem.assignedTo}</DetailValue>
            </DetailRow>
          )}

          {partiesData && (
            <DetailRow>
              <DetailLabel minWidth="110px" mobileMinWidth="70px">
                Parties:
              </DetailLabel>
              <DetailValue>
                {partiesData.text}
                {partiesData.remaining > 0 && (
                  <MoreCount> +{partiesData.remaining} more</MoreCount>
                )}
              </DetailValue>
            </DetailRow>
          )}

          {attorneysData && (
            <DetailRow>
              <DetailLabel minWidth="110px" mobileMinWidth="70px">
                Attorneys:
              </DetailLabel>
              <DetailValue>
                {attorneysData.text}
                {attorneysData.remaining > 0 && (
                  <MoreCount> +{attorneysData.remaining} more</MoreCount>
                )}
              </DetailValue>
            </DetailRow>
          )}
        </DetailsContainer>
      </ContentContainer>

      <ViewButton variant="outlined" onClick={handleViewClick}>
        View
      </ViewButton>
    </CardContainer>
  );
};
